import { create } from "zustand";
import { api } from "../api";

export const useAssetStore = create((set, get) => ({
  // State
  servers: [],
  routers: [],
  discoveredAssets: [],
  summaryStats: null,
  loading: false,
  error: null,
  lastUpdated: null,

  // Fetch Network Data (/network/routers & /network/summary)
  fetchNetworkData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const [routersData, summaryData] = await Promise.all([
        api("/network/routers"),
        api("/network/summary"),
      ]);
      set({
        routers: routersData || [],
        summaryStats: summaryData || null,
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({
          error: "Failed to load network infrastructure data.",
          loading: false,
        });
      }
    }
  },

  // Fetch CMDB Servers Data (/cmdb/servers)
  fetchCmdbData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const serversData = await api("/cmdb/servers");
      set({
        servers: serversData || [],
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Failed to load CMDB servers.", loading: false });
      }
    }
  },

  // Fetch Staging Discovered Assets Data (/discovery/assets)
  fetchDiscoveryData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const assetsData = await api("/discovery/assets");
      set({
        discoveredAssets: assetsData || [],
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Failed to load discovered assets.", loading: false });
      }
    }
  },

  // Master Parallel Fetch (All Views Synced Simultaneously)
  fetchAllData: async (isSilent = false) => {
    if (!isSilent) set({ loading: true, error: null });
    try {
      const [routersData, summaryData, serversData, assetsData] =
        await Promise.allSettled([
          api("/network/routers"),
          api("/network/summary"),
          api("/cmdb/servers"),
          api("/discovery/assets"),
        ]);

      set({
        routers:
          routersData.status === "fulfilled" ? routersData.value || [] : get().routers,
        summaryStats:
          summaryData.status === "fulfilled" ? summaryData.value || null : get().summaryStats,
        servers:
          serversData.status === "fulfilled" ? serversData.value || [] : get().servers,
        discoveredAssets:
          assetsData.status === "fulfilled" ? assetsData.value || [] : get().discoveredAssets,
        lastUpdated: new Date().toISOString(),
        loading: false,
      });
    } catch (err) {
      if (!isSilent) {
        set({ error: "Global infrastructure refresh error.", loading: false });
      }
    }
  },

  // Real-time Stream & WebSocket Atomic Patch Action
  updateLiveTelemetry: (hostnameOrIp, telemetryPatch) => {
    const key = (hostnameOrIp || "").toLowerCase();
    if (!key) return;

    set((state) => {
      const matchKey = (item) =>
        (item?.hostname || "").toLowerCase() === key ||
        (item?.ip_address || "").toLowerCase() === key ||
        (item?.specs?.ip_address || "").toLowerCase() === key;

      const routersArr = Array.isArray(state.routers) ? state.routers : [];
      const updatedRouters = routersArr.map((r) => {
        if (matchKey(r)) {
          return {
            ...r,
            ...telemetryPatch,
            specs: { ...(r.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return r;
      });

      const serversArr = Array.isArray(state.servers) ? state.servers : [];
      const updatedServers = serversArr.map((s) => {
        if (matchKey(s)) {
          return {
            ...s,
            ...telemetryPatch,
            specs: { ...(s.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return s;
      });

      const assetsArr = Array.isArray(state.discoveredAssets)
        ? state.discoveredAssets
        : [];
      const updatedAssets = assetsArr.map((a) => {
        if (matchKey(a)) {
          return {
            ...a,
            ...telemetryPatch,
            specs: { ...(a.specs || {}), ...(telemetryPatch.specs || {}) },
          };
        }
        return a;
      });

      return {
        routers: updatedRouters,
        servers: updatedServers,
        discoveredAssets: updatedAssets,
        lastUpdated: new Date().toISOString(),
      };
    });
  },
}));
